import { Flex, Skeleton, Typography } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { ActivityFeedSection } from "./ActivityFeedSection.tsx"
import { ProjectSnapshotSection } from "./ProjectSnapshotSection.tsx"
import { QuickActionsSection } from "./QuickActionsSection.tsx"
import { TopSummarySection } from "./TopSummarySection.tsx"
import {
  buildProjectSnapshots,
  buildTopSummary,
  drillToPath,
  getRelativeTimeParts,
  orderActivityItems,
  readMetricValue,
  resolveQuickActions
} from "./_helpers.ts"
import { useMobileHomeData } from "./useMobileHomeData.ts"

export const MobileHome = () => {
  const { t } = useTranslation()

  const userInfo = useAppSelector((state) => state.auth.userInfo)
  const permissionCodes = useAppSelector((state) => state.auth?.permissions?.codes)
  const unreadCount = useAppSelector((state) => state.notification.unreadCount)

  const {
    isUnavailable,
    isLoading,
    allValues,
    projects,
    tasksDue,
    milestoneRows,
    activityItems
  } = useMobileHomeData()

  const summaryStats = useMemo(
    () =>
      buildTopSummary({
        unreadCount: unreadCount ?? 0,
        activeProjects: readMetricValue(allValues, "active_projects"),
        pendingApprovals: readMetricValue(allValues, "pending_approvals"),
        tasksDue
      }),
    [unreadCount, allValues, tasksDue]
  )

  const quickActions = useMemo(
    () =>
      resolveQuickActions({
        canCreateLead: !!permissionCodes?.leads?.leads?.lead_create,
        canCreateProject: !!permissionCodes?.projects?.projects?.project_create
      }),
    [permissionCodes]
  )

  const snapshots = useMemo(
    () => buildProjectSnapshots(projects, milestoneRows),
    [projects, milestoneRows]
  )

  const feedItems = useMemo(
    () =>
      orderActivityItems(activityItems).map((item) => {
        const { unit, count } = getRelativeTimeParts(item.timestamp)
        return {
          id: String(item.id),
          title: item.title,
          relative:
            unit === "now"
              ? t("mobileHome.feed.now", "just now")
              : t(`mobileHome.feed.${unit}`, { count }),
          to: drillToPath(item.drill_to)
        }
      }),
    [activityItems, t]
  )

  if (isUnavailable) return null

  return (
    <Flex vertical gap={"middle"} data-testid={"mobile-home"}>
      <Typography.Title level={4} className={"!mb-0"}>
        {t("mobileHome.greeting", "Welcome, {{name}}!", {
          name: userInfo?.first_name || ""
        })}
      </Typography.Title>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          <TopSummarySection
            stats={summaryStats}
            labelFor={(stat) => t(stat.labelKey, stat.fallback)}
          />
          <QuickActionsSection
            title={t("mobileHome.actions.title", "Quick Actions")}
            actions={quickActions}
            labelFor={(action) => t(action.labelKey, action.fallback)}
          />
          <ProjectSnapshotSection
            title={t("mobileHome.snapshot.title", "Project Snapshot")}
            emptyText={t("mobileHome.snapshot.empty", "No active projects")}
            budgetLabel={t("mobileHome.snapshot.budget", "Budget")}
            nextMilestoneLabel={t("mobileHome.snapshot.nextMilestone", "Next")}
            snapshots={snapshots}
          />
          <ActivityFeedSection
            title={t("mobileHome.feed.title", "Activity Feed")}
            emptyText={t("mobileHome.feed.empty", "No recent activity")}
            items={feedItems}
          />
        </>
      )}
    </Flex>
  )
}
